import clsx from "clsx";
import { FC, Ref, useEffect, useRef } from "react";

import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import {
  ClickEvent,
  Menu,
  MenuDirection,
  MenuInstance,
  MenuItem,
  MenuType,
  SubMenu,
} from "mds/components/Menu";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { ChevronDownIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCourseCodes } from "store/selectors";

export type CourseMenuProps = {
  menuClassName?: string;
  selectedCourseCodeId: string;
  onCourseCodeChange: (courseCodeId: string) => void;
  menuType?: MenuType;
  defaultToAll?: boolean;
  size?: "xs" | "s";
  direction?: MenuDirection;
};

// This is very similar to TermMenu, consider refactoring to a shared general dropdown component
export const CourseCodeMenu: FC<CourseMenuProps> = ({
  menuClassName,
  selectedCourseCodeId,
  onCourseCodeChange,
  menuType = "submenu",
  defaultToAll = true,
  size = "xs",
  direction = "bottom",
}) => {
  const subMenuRef: Ref<MenuInstance> = useRef(null);
  const courseCodes = useAppSelector(selectCourseCodes).toSorted(ORDER_BY_TITLE);
  const defaultText = defaultToAll
    ? t("import_modal.all_course_codes")
    : t("import_modal.select_course_code");
  const courseCodeOptions = [{ id: "", title: defaultText }, ...courseCodes];
  const selectedCourseCode = courseCodeOptions.find(
    (courseCode) => courseCode?.id === selectedCourseCodeId,
  );

  useEffect(() => {
    storeApi.course_codes.list();
  }, []);

  const onItemClick = (e: ClickEvent) => {
    const newCourseCode = e.value as CourseType;
    onCourseCodeChange(newCourseCode.id);
    // This is a supported feature of szhsin/react-menu which allows
    // the menu to stay open even if a menu item is clicked. We use this to keep parent menus open
    // eslint-disable-next-line no-param-reassign
    e.keepOpen = true;
    subMenuRef.current.closeMenu();
  };

  const menuButton = (
    <LegacyButton className="w-full gap-1" kind="secondary" size={size}>
      <IconText iconStart={<TextBooksIcon />} text={selectedCourseCode?.title} />
      <ChevronDownIcon />
    </LegacyButton>
  );

  const DropdownMenu = courseCodeOptions.map((courseCodeOption) => {
    const selected = selectedCourseCodeId === courseCodeOption.id;
    return (
      <MenuItem
        checked={selected}
        className={clsx({ selected, "text-white": selected }, "center dropdown-item")}
        key={courseCodeOption.id}
        value={courseCodeOption}
        onClick={onItemClick}
      >
        <IconText iconStart={<TextBooksIcon />} text={courseCodeOption.title} />
      </MenuItem>
    );
  });

  const menuProps = {
    className: "dropdown",
    instanceRef: subMenuRef,
    children: DropdownMenu,
    size,
    direction,
  };

  return menuType === "menu" ? (
    <Menu
      {...menuProps}
      direction={direction}
      menuButton={menuButton}
      menuClassName={menuClassName}
      position="initial"
    />
  ) : (
    <SubMenu
      {...menuProps}
      direction={direction}
      label={menuButton}
      openTrigger="clickOnly"
      position="initial"
    />
  );
};
